import posthog, { CaptureResult, Properties } from "posthog-js";

export type PostHogProperties = Properties;

/** Runtime settings supplied by the host app. */
export interface PostHogConfig {
  // Requiring the ingestion host avoids silently sending data to PostHog's US
  // region when the deployment intended another region.
  projectToken?: string;
  host?: string;
  app: string;
  appVersion: string;
  flavor?: string;
  debug?: boolean;
  optOut?: boolean;
}

/**
 * Narrow adapter around the PostHog singleton so analytics behavior can be
 * tested without sending events.
 */
export interface PostHogClient {
  init: (token: string, options: PostHogInitOptions) => void;
  capture: (event: string, properties?: PostHogProperties) => void;
  identify: (userId: string, userProperties?: PostHogProperties) => void;
  register: (properties: PostHogProperties) => void;
  unregister: (property: string) => void;
  reset: () => void;
  optIn: () => void;
  optOut: () => void;
}

export interface PostHogInitOptions {
  api_host: string;
  autocapture: boolean;
  before_send: (event: CaptureResult | null) => CaptureResult | null;
  capture_dead_clicks: boolean;
  capture_exceptions: boolean;
  capture_heatmaps: boolean;
  capture_pageleave: boolean;
  capture_pageview: boolean;
  capture_performance: boolean;
  advanced_disable_feature_flags: boolean;
  debug: boolean;
  disable_session_recording: boolean;
  disable_surveys: boolean;
  ip: false;
  mask_personal_data_properties: boolean;
  opt_out_capturing_by_default: boolean;
  person_profiles: "identified_only";
  persistence: "memory";
}

const postHogClient: PostHogClient = {
  init: (token, options) => {
    posthog.init(token, options);
  },
  capture: (event, properties) => {
    posthog.capture(event, properties);
  },
  identify: (userId, userProperties) => {
    posthog.identify(userId, userProperties);
  },
  register: (properties) => {
    posthog.register(properties);
  },
  unregister: (property) => {
    posthog.unregister(property);
  },
  reset: () => {
    posthog.reset();
  },
  optIn: () => {
    posthog.opt_in_capturing();
  },
  optOut: () => {
    posthog.opt_out_capturing();
  },
};

const SENSITIVE_PROPERTY_NAMES = new Set([
  "authorization",
  "email",
  "name",
  "password",
  "token",
  "user_name",
  "username",
]);
const REDACTED = "[redacted]";
// The SDK adds page data to events and person properties independently. Strip
// both before adding normalized routes back to pageview event properties.
const PRIVATE_PAGE_PROPERTY_NAMES = new Set([
  "$current_url",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer",
  "$pathname",
  "$prev_pageview_pathname",
  "$referrer",
  "$session_entry_referrer",
  "$session_entry_pathname",
  "$session_entry_url",
  "title",
]);
// The SDK copies these query values into event, session-entry, and initial
// person properties. Mirrors posthog-js's campaign parameter list.
const CAMPAIGN_PROPERTY_NAMES = new Set([
  "_kx",
  "dclid",
  "epik",
  "fbclid",
  "gad_source",
  "gbraid",
  "gclid",
  "gclsrc",
  "igshid",
  "irclid",
  "li_fat_id",
  "mc_cid",
  "msclkid",
  "ph_keyword",
  "qclid",
  "rdt_cid",
  "sccid",
  "ttclid",
  "twclid",
  "utm_campaign",
  "utm_content",
  "utm_medium",
  "utm_source",
  "utm_term",
  "wbraid",
]);

const isPrivatePageProperty = (key: string): boolean =>
  PRIVATE_PAGE_PROPERTY_NAMES.has(key) ||
  CAMPAIGN_PROPERTY_NAMES.has(key.replace(/^\$(initial|session_entry)_/, ""));

const redact = (
  properties: PostHogProperties | undefined
): PostHogProperties | undefined => {
  if (!properties) {
    return properties;
  }
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !isPrivatePageProperty(key))
      .map(([key, value]) => [
        key,
        key === "$set" || key === "$set_once"
          ? redact(value)
          : SENSITIVE_PROPERTY_NAMES.has(key.toLowerCase())
          ? REDACTED
          : value,
      ])
  );
};

const redactEvent = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) {
    return event;
  }
  const projectToken = event.properties.token;
  const properties = redact(event.properties) ?? {};
  const routePattern = properties.route_pattern;
  // The SDK adds the public project token after callers provide their event
  // properties. Ingestion needs that value to route the event to this project.
  if (projectToken !== undefined) {
    properties.token = projectToken;
  }
  return {
    ...event,
    properties: {
      ...properties,
      // `ip: false` prevents storing the raw address, while this processing
      // sentinel also prevents PostHog from deriving location properties.
      $geoip_disable: true,
      // Pageviews use a normalized route; other events omit URLs entirely so
      // record IDs, query values, and referrers stay private.
      ...(typeof routePattern === "string"
        ? { $current_url: routePattern, $pathname: routePattern }
        : {}),
    },
    $set: redact(event.$set),
    $set_once: redact(event.$set_once),
  };
};

export interface PostHogIdentity {
  userId: string;
  role: string;
  isInternal?: boolean;
  customerUuid?: string;
}

/**
 * Product analytics entry point, mirroring `shared_flutter`'s `AnalyticsService`
 * so events line up with the companion and operator apps.
 *
 * The service is deliberately a no-op until configured. It accepts stable,
 * non-PII user identifiers and RBAC context, but does not accept names, email
 * addresses, access tokens, or other profile fields.
 */
export class PostHogService {
  constructor(private readonly client: PostHogClient = postHogClient) {}

  private enabled = false;
  private userId?: string;
  private identityKey?: string;
  private appProperties: PostHogProperties = {};

  get isEnabled(): boolean {
    return this.enabled;
  }

  setup(config: PostHogConfig): void {
    const token = config.projectToken?.trim();
    const host = config.host?.trim();
    if (!token || !host) {
      this.enabled = false;
      return;
    }

    this.client.init(token, {
      api_host: host,
      autocapture: false,
      before_send: redactEvent,
      // Pin these off so enabling them in PostHog project settings cannot
      // start collecting in the portal without a code change.
      capture_dead_clicks: false,
      capture_exceptions: false,
      capture_heatmaps: false,
      capture_pageleave: false,
      capture_pageview: false,
      capture_performance: false,
      advanced_disable_feature_flags: true,
      debug: config.debug ?? false,
      disable_session_recording: true,
      disable_surveys: true,
      ip: false,
      mask_personal_data_properties: true,
      opt_out_capturing_by_default: config.optOut ?? false,
      person_profiles: "identified_only",
      // Avoid cookies/localStorage. The signed-in identity is restored from
      // application state after each page load.
      persistence: "memory",
    });
    this.enabled = true;
    this.appProperties = {
      app: config.app,
      app_version: config.appVersion,
      ...(config.flavor ? { flavor: config.flavor } : {}),
    };
    this.client.register(this.appProperties);
  }

  identify({ userId, role, isInternal, customerUuid }: PostHogIdentity): void {
    if (!this.enabled || !userId) {
      return;
    }
    const identityKey = `${userId}:${role}:${isInternal}:${customerUuid}`;
    if (this.identityKey === identityKey) {
      return;
    }
    // A different user in the same tab must not inherit the previous identity.
    if (this.userId && this.userId !== userId) {
      this.client.reset();
      this.client.register(this.appProperties);
    }
    this.client.identify(userId, {
      rbac_role: role,
      ...(isInternal === undefined ? {} : { is_internal: isInternal }),
      ...(customerUuid === undefined ? {} : { customer_uuid: customerUuid }),
    });
    if (isInternal === undefined) {
      this.client.unregister("is_internal");
    }
    if (customerUuid === undefined) {
      this.client.unregister("customer_uuid");
    }
    this.client.register({
      rbac_role: role,
      ...(isInternal === undefined ? {} : { is_internal: isInternal }),
      ...(customerUuid === undefined ? {} : { customer_uuid: customerUuid }),
    });
    this.userId = userId;
    this.identityKey = identityKey;
  }

  /** Records a route view; automatic pageview capture stays off. */
  page(name: string, properties?: PostHogProperties): void {
    if (!this.enabled || !name) {
      return;
    }
    this.client.capture("$pageview", {
      ...redact(properties),
      screen_name: name,
    });
  }

  capture(event: string, properties?: PostHogProperties): void {
    if (!this.enabled || !event) {
      return;
    }
    this.client.capture(event, redact(properties));
  }

  reset(): void {
    this.userId = undefined;
    this.identityKey = undefined;
    if (this.enabled) {
      this.client.reset();
      this.client.register(this.appProperties);
    }
  }

  setCollectionEnabled(enabled: boolean): void {
    if (!this.enabled) {
      return;
    }
    if (enabled) {
      this.client.optIn();
    } else {
      this.client.optOut();
    }
  }
}

export const postHog = new PostHogService();
